import styled from 'styled-components/macro';
import classNames from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import { ReactComponent as ArrowRight } from '../../assets/arrow-right.svg';
import TechSectorIcon from './tech-sector-icon';
import { formatBuildingRoute } from '../utils/router';
import useTranslations from '../utils/language-provider';
import Tooltip from './tooltip';
import { buildings } from '../../config/building-config';
import { HotspotConfig } from '../../config/city';
import { SectorListPositions } from '../../interfaces/buildings';
import NewFlag from './newflag';
import { ReactComponent as Pin } from '../../assets/pin.svg';
import { isMobileDevice } from '../utils/mobile-detection';
import { BuildingType } from '../../config/buildings';

export type TooltipType = 'inactive' | 'secondary';
export type ArrowAlignType = 'center' | 'left';
export type LabelStyle = 'primary' | 'secondary';
export type LabelText = 'comingSoonBuilding' | 'newBuilding';

type HotspotFlagProps = {
    style?: React.CSSProperties;
    className?: string;
    onClickTooltip?(): void;
    config: HotspotConfig;
};

type HotspotFlagElementProps = {
    arrowAlign: ArrowAlignType;
    y?: number;
    flagDown?: boolean;
    colorsInverted?: boolean;
};

type HotspotWrapperProps = {
    type?: TooltipType;
    arrowAlign?: ArrowAlignType;
};

const HoverTarget = styled.div<{ y: number }>`
    position: absolute;
    width: 100%;
    height: 100%;
    top: ${props => props.y > 350 ? '-50px' : '35px'};
    left: 15px;
`;

export const HotspotFlagElement = styled.div<HotspotFlagElementProps>`
    position: relative;
    text-align: center;

    .inner {
        height: 32px;
        position: relative;
        z-index: ${(props) => props.theme.zIndex.content};
        min-width: 120px;
        padding: 0px 10px;
        display: flex;
        align-items: ${(props) => (props.flagDown || (props.y !== undefined && props.y < 350) ? 'end' : 'start')};
        justify-content: center;
        gap: 5px;
        color: ${(props) => (props.colorsInverted ? props.theme.colors.primary : props.theme.colors.white)};
        background: ${(props) => (props.colorsInverted ? props.theme.colors.white : props.theme.colors.primary80)};
        font-size: ${(props) => props.theme.typography.sm};
        font-weight: bold;
        overflow: visible;
        transition: all 100ms ease-in;
        left: 10px;
        top: ${(props) => (props.flagDown || (props.y !== undefined && props.y < 350) ? '25px;' : '-25px;')}
        clip-path: ${(props) => {
            const isBottomArrow = props.flagDown || (props.y !== undefined && props.y < 350);
            const isCentered = props.arrowAlign === 'center';

            if (isBottomArrow && isCentered) {
                return 'polygon(0% 0%, 100% 0%, 100% 75%, 50% 100%, 0% 75%)';
            } else if (!isBottomArrow && isCentered) {
                return 'polygon(50% 0%, 100% 25%, 100% 100%, 0% 100%, 0% 25%)';
            } else if (isBottomArrow && !isCentered) {
                return 'polygon(0% 0%, 15% 45%, 100% 45%, 100% 100%, 0% 100%)';
            } else {
                return 'polygon(0% 0%, 100% 0%, 100% 55%, 15% 55%, 0% 100%)';
            }
        }};

        @media ${(props) => props.theme.screens.lg} {
            align-items: ${(props) => (props.y !== undefined && props.y < 350 ? 'end' : 'start')};
            height: 50px;
            top: ${(props) => (props.y !== undefined && props.y < 350 ? '35px;' : '-48px;')}
            left: 15px;
            font-size: ${(props) => props.theme.typography.md};
            padding: 2px 10px;
            min-width: 168px;
            clip-path: ${(props) => {
                const isBottomArrow = props.y !== undefined && props.y > 350;
                const isCentered = props.arrowAlign === 'center';

                if (isBottomArrow && isCentered) {
                    return 'polygon(50% 0%, 100% 45%, 100% 100%, 0% 100%, 0% 45%)';
                } else if (isBottomArrow && !isCentered) {
                    return 'polygon(0% 0%, 100% 0%, 100% 55%, 12% 55%, 0% 100%)';
                } else if (!isBottomArrow && isCentered) {
                    return 'polygon(0% 0%, 100% 0%, 100% 55%, 50% 100%, 0% 55%)';
                } else {
                    return 'polygon(0% 0%, 12% 45%, 100% 45%, 100% 100%, 0% 100%)';
                }
            }};
        }
    }
`;

export const TechSectorList = styled.div<{ sectorPosition: SectorListPositions }>`
    display: flex;
    width: 100%;
    position: absolute;
    z-index: ${(props) => props.theme.zIndex.tooltip};
    top: ${(props) => (props.sectorPosition === 'top' ? '-45px' : '58px')};
    left: 11px;

    a {
        flex-grow: 1;
        pointer-events: ${isMobileDevice() ? 'none' : 'auto'};
    }

    @media ${(props) => props.theme.screens.lg} {
        top: ${(props) => (props.sectorPosition === 'top' ? '-72px' : '88px')};
        left: 17px;
    }
`;

export const StyledTechSectorButton = styled(TechSectorIcon)<{ sectorPosition: SectorListPositions }>`
    top: ${(props) => (props.sectorPosition === 'top' ? '27px' : '-33px')};
    opacity: 0;
    transition: opacity 100ms ease-in, top 100ms ease-in;
`;

export const HotspotWrapper = styled.div<HotspotWrapperProps>`
    position: absolute;
    margin-top: -20px;
    margin-left: ${(props) => (props.arrowAlign === 'center' ? '-64px' : '-15px')};

    @media ${(props) => props.theme.screens.lg} {
        margin-top: -25px;
    }

    &.secondary {
        ${HotspotFlagElement} {
            .inner {
                background: ${(props) => props.theme.colors.secondary};
            }
        }
    }

    &.inactive {
        margin-top: -62px;

        @media ${(props) => props.theme.screens.lg} {
            margin-top: -102px;
        }

        ${HotspotFlagElement} {
            cursor: default !important;

            .inner {
                background: ${(props) => props.theme.colors.gray1};
                border-radius: 0 0 3px 3px;
            }
        }
    }


    &:hover {
        ${HotspotFlagElement}, ${TechSectorList} {
        transform-origin: left bottom;
        transform: scale(1.1);
        transition: transform 100ms ease-in;}

        ${HotspotFlagElement} {
            cursor: pointer;
        }

        &.secondary {
            ${HotspotFlagElement} {
                .inner {
                    background: ${(props) => props.theme.colors.secondaryLight};
                }
            }
        }

        &.inactive {
            ${HotspotFlagElement} {
                .inner {
                    background: ${(props) => props.theme.colors.gray1};
                    border-radius: 0 0 3px 3px;
                }
            }
        }

        ${StyledTechSectorButton} {
            opacity: 1;
            top: 0;
        }

        ${NewFlag}{
            top: 73px;
            transition: top 300ms, left 300ms;
        }

        @media ${(props) => props.theme.screens.lg} {
            ${NewFlag}{
                top: 103px;
                left: 43px;
            }
        }
    }
`;

const ArrowIcon = styled.div<{ invertColor?: boolean }>`
    flex-shrink: 0;
    position: relative;
    right: 0;

    svg {
        position: relative;
        height: 12px;

        & path {
            stroke: ${(props) => (props.invertColor ? props.theme.colors.primary : props.theme.colors.white)};
            fill: ${(props) => (props.invertColor ? props.theme.colors.primary : props.theme.colors.white)};
        }
    }

`;

const Label = styled.div<{ styleType: LabelStyle }>`
    display: inline-block;
    font-size: 10px;
    color: #fff;
    background: ${(props) => (props.styleType === 'primary' ? props.theme.colors.primary : props.theme.colors.secondary)};
    border-radius: 3px;
    padding: 2px 4px 3px;
    box-shadow: 0 5px 10px rgb(0, 0, 0, .6);

    @media ${(props) => props.theme.screens.lg} {
        font-size: 14px;
        padding: 2px 8px 3px;
    }
`;

export const PinWrapper = styled.div<{ coordinates: { x: number; y: number }; isMobile: boolean }>`
    position: absolute;
    height: 20px;
    aspect-ratio: 1 / 1;
    transform-origin: top;

    @keyframes pulse {
        0% {
            transform: scale(1);
        }
        100% {
            transform: ${(props) => (props.isMobile ? 'scale(1.05)' : 'scale(1.2)')};
        }
    }

    animation: pulse 2.2s alternate ease-in-out infinite;
    animation-delay: ${(props) => props.coordinates.x * 5}ms;

    @media ${(props) => props.theme.screens.lg} {
        height: 30px;
    }
`;

const HotspotFlag = ({ style = {}, className, onClickTooltip, config }: HotspotFlagProps) => {
    const translations = useTranslations();
    const history = useHistory();
    const {
        type,
        labelText,
        labelStyle = 'primary',
        arrowAlign = 'left',
        building,
        showSector = true,
        showNewFlag = false,
        isBigFlag = false,
    } = config;
    const label = translations.buildings[building].name;
    const sectors = buildings[building]?.techSectors || [];
    const sectorPosition = buildings[building]?.sectorPosition || 'top';
    const showTechSectors = showSector === true && type !== 'inactive' && !!sectors.length;

    return (
        <HotspotWrapper
            style={style}
            arrowAlign={arrowAlign}
            type={type}
            className={classNames(className, {
                secondary: type === 'secondary',
                inactive: type === 'inactive',
            })}
        >
            <PinWrapper coordinates={config.coordinates} isMobile={isMobileDevice()}>
                <Pin />
            </PinWrapper>

            {showNewFlag && !isBigFlag && <NewFlag isBottom={config.coordinates.y < 350} type='building'>{translations.buildingDetails.new}</NewFlag>}

            {showTechSectors && (!isMobileDevice() || isBigFlag) && (
                <TechSectorList sectorPosition={sectorPosition}>
                    {sectors.map((sector, index) => {
                        const btnProps = {
                            sectorType: sector.type,
                            style: {
                                transitionDelay: `${100 * index}ms`,
                            },
                        };

                        if (sector.comingSoon) {
                            return (
                                <Tooltip content={translations.city.comingSoonTechSector} key={sector.type}>
                                    {({ triggerRef }) => (
                                        <StyledTechSectorButton
                                            {...btnProps}
                                            sectorPosition={sectorPosition}
                                            inactive
                                            ref={triggerRef}
                                            newSector={sector.newSector}
                                            isBigFlag={isBigFlag}
                                        />
                                    )}
                                </Tooltip>
                            );
                        }

                        const sectorRoute = formatBuildingRoute({ building, techSector: sector.type });

                        return (
                            <Link key={sector.type} to={sectorRoute}>
                                <Tooltip
                                    content={translations.sectors[sector.type].name}
                                    onContentClick={() => history.push(sectorRoute)}
                                >
                                    {({ triggerRef }) => (
                                        <StyledTechSectorButton
                                            {...btnProps}
                                            sectorPosition={sectorPosition}
                                            ref={triggerRef}
                                            newSector={sector.newSector}
                                            isBigFlag={isBigFlag}
                                        />
                                    )}
                                </Tooltip>
                            </Link>
                        );
                    })}
                </TechSectorList>
            )}

            <HotspotFlagElement
                arrowAlign={arrowAlign}
                onClick={() => onClickTooltip?.()}
                y={config.coordinates.y}
                flagDown={isMobileDevice() && config.building === BuildingType.SKYSCRAPER}
                colorsInverted={config.building === BuildingType.KSB_QUICKLINKS}
            >
                {labelText && <Label styleType={labelStyle}>{translations.city[labelText]}</Label>}

                {!isBigFlag && <HoverTarget y={config.coordinates.y} />}
                <div className='inner'>
                    {label}

                    {type !== 'secondary' && (
                        <ArrowIcon invertColor={config.building === BuildingType.KSB_QUICKLINKS}>
                            <ArrowRight />
                        </ArrowIcon>
                    )}
                </div>
            </HotspotFlagElement>
        </HotspotWrapper>
    );
};

export default HotspotFlag;
